export type ImportIssueSeverity = 'error' | 'warning';

export const IMPORT_ISSUE_CODES = [
  'empty_file',
  'file_too_large',
  'missing_header_column',
  'unknown_header_column',
  'row_limit_exceeded',
  'encoding_error',
  'required_missing',
  'too_long',
  'invalid_email',
  'contact_required',
  'invalid_date',
  'future_date',
  'invalid_sex',
  'invalid_boolean',
  'invalid_weight',
  'unknown_species',
  'unknown_warning',
  'duplicate_pet_in_file',
  'owner_field_conflict',
  'existing_client_email',
] as const;

export const IMPORT_COLUMN_KEYS = [
  'owner_key',
  'owner_full_name',
  'owner_email',
  'owner_phone',
  'owner_address_line',
  'owner_city',
  'owner_postal_code',
  'owner_country',
  'pet_name',
  'pet_species',
  'pet_breed',
  'pet_sex',
  'pet_date_of_birth',
  'pet_weight',
  'pet_neutered',
  'pet_main_issue',
  'pet_diagnosis',
  'pet_medication',
  'pet_past_surgeries',
  'pet_allergies',
  'pet_notes',
  'pet_warnings',
] as const;

export interface ImportIssueApiResource {
  row_number: number;
  column: string | null;
  severity: ImportIssueSeverity;
  code: string;
  message: string;
}

export interface ImportIssue {
  rowNumber: number;
  column: string | null;
  severity: ImportIssueSeverity;
  code: string;
  message: string;
}

export interface ImportPreviewRowApiResource {
  row_number: number;
  cells: Record<string, string>;
  issues: ImportIssueApiResource[];
}

export interface ImportPreviewRow {
  rowNumber: number;
  cells: Record<string, string>;
  issues: ImportIssue[];
}

export interface ImportSummaryApiResource {
  total_rows: number;
  client_count: number;
  pet_count: number;
  error_count: number;
  warning_count: number;
  is_committable: boolean;
}

export interface ImportSummary {
  totalRows: number;
  clientCount: number;
  petCount: number;
  errorCount: number;
  warningCount: number;
  isCommittable: boolean;
}

export interface ImportPreviewApiResource {
  columns: string[];
  rows: ImportPreviewRowApiResource[];
  summary: ImportSummaryApiResource;
}

export interface ImportPreview {
  columns: string[];
  rows: ImportPreviewRow[];
  summary: ImportSummary;
}

export interface ImportCommitResultApiResource {
  created_client_count: number;
  attached_existing_client_count: number;
  created_pet_count: number;
  idempotency_key: string;
}

export interface ImportCommitResult {
  createdClientCount: number;
  attachedExistingClientCount: number;
  createdPetCount: number;
  idempotencyKey: string;
}

export function mapApiResourceToImportIssue(resource: ImportIssueApiResource): ImportIssue {
  return {
    rowNumber: resource.row_number,
    column: resource.column,
    severity: resource.severity,
    code: resource.code,
    message: resource.message,
  };
}

export function mapApiResourceToImportPreviewRow(
  resource: ImportPreviewRowApiResource
): ImportPreviewRow {
  return {
    rowNumber: resource.row_number,
    cells: resource.cells,
    issues: resource.issues.map(mapApiResourceToImportIssue),
  };
}

export function mapApiResourceToImportSummary(resource: ImportSummaryApiResource): ImportSummary {
  return {
    totalRows: resource.total_rows,
    clientCount: resource.client_count,
    petCount: resource.pet_count,
    errorCount: resource.error_count,
    warningCount: resource.warning_count,
    isCommittable: resource.is_committable,
  };
}

export function mapApiResourceToImportPreview(resource: ImportPreviewApiResource): ImportPreview {
  return {
    columns: resource.columns,
    rows: resource.rows.map(mapApiResourceToImportPreviewRow),
    summary: mapApiResourceToImportSummary(resource.summary),
  };
}

export function mapImportPreviewRowToCommitPayload(
  row: ImportPreviewRow
): { row_number: number; cells: Record<string, string> } {
  return { row_number: row.rowNumber, cells: row.cells };
}

export function mapApiResourceToImportCommitResult(
  resource: ImportCommitResultApiResource
): ImportCommitResult {
  return {
    createdClientCount: resource.created_client_count,
    attachedExistingClientCount: resource.attached_existing_client_count,
    createdPetCount: resource.created_pet_count,
    idempotencyKey: resource.idempotency_key,
  };
}
